import React, { ChangeEvent, KeyboardEvent, MouseEvent, useEffect, useState } from 'react';

import cn from 'classnames';

import { Text, Toggle } from '@components/common';
import { CloseSVG, InfoCircleIcon } from '@components/svgs';
import { removeListDecorators } from '@components/utils';
import { Tooltip } from 'components/Tooltip';

import * as BlockableComponents from '../../../types/blockable-components';

export const Options = <M extends 'single' | 'multiple' = 'single'>({
  mode,
  value: initialValue,
  options: initialOptions,
  isEditable,
  wrapperClassName,
  onChange,
  onChangeOptions,
  onChangeOther,
  onChangeNoneOfAbove,
  other,
  none_of_above
}: BlockableComponents.Options<M>) => {
  const [value, setValue] = useState<string | string[]>(initialValue || (mode === 'single' ? '' : []));
  const [options, setOptions] = useState<string[]>(initialOptions || []);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const [otherSelected, setOtherSelected] = useState<boolean>(false);
  const [otherValue, setOtherValue] = useState<string>('');

  const setOptionsWithCallback = (newOptions: string[]) => {
    setOptions(newOptions);
    onChangeOptions?.(newOptions);
  };

  const setValueWithCallback = (newValue: string | string[]) => {
    setValue(newValue);
    onChange?.(newValue);
  };

  const focusOption = (index: number) => {
    const input = document.getElementById(`option-${index}-label`);
    input?.focus();
  };

  const addOptionAndFocus = (option = '') => {
    setOptionsWithCallback([...options, option]);
    requestAnimationFrame(() => focusOption(options.length));
  };

  const handleOnOptionChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    if (mode === 'single' && value === options[index]) {
      setValueWithCallback('');
    }

    const newOptions = [...options];
    newOptions[index] = e.target.value;
    setOptionsWithCallback(newOptions);
  };

  const handleOnKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Enter') {
      const newOptions = [...options];
      const newIndex = index + 1;

      newOptions.splice(newIndex, 0, '');
      setOptionsWithCallback(newOptions);

      requestAnimationFrame(() => focusOption(newIndex));
    } else if (e.key === 'Backspace' && options[index] === '' && options.length > 1) {
      const newOptions = [...options];
      newOptions.splice(index, 1);
      setOptionsWithCallback(newOptions);

      requestAnimationFrame(() => focusOption(newOptions.length - 1));
    }
  };

  const handleOnPaste = (e: React.ClipboardEvent<HTMLInputElement>, index: number) => {
    const text = e.clipboardData.getData('text');

    if (text.includes('\n')) {
      e.preventDefault();

      const newOptions = text.split('\n').map(removeListDecorators);

      if (newOptions.length > 1) {
        const updatedOptions = [...options];
        updatedOptions.splice(index, 1, ...newOptions);
        setOptionsWithCallback(updatedOptions);
      }
    }
  };

  const handleOnValueChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const isChecked = e.target.checked;

    if (mode === 'single' && typeof value === 'string') {
      if (otherSelected) {
        // reset the 'other' value if the input is unselected
        setOtherSelected(false);
        setOtherValue('');
      }

      setValueWithCallback(newValue);
    } else if (mode === 'multiple' && Array.isArray(value)) {
      setValue((prev: string[]) => {
        const filteredPrev = prev.filter((v) => v !== 'None of the above');
        const newValues = isChecked ? [...filteredPrev, newValue] : value.filter((v) => v !== newValue);
        onChange?.(newValues);

        return newValues;
      });
    }
  };

  const handleOnOtherValueChange = (newValue: string) => {
    if (mode === 'single') {
      setOtherValue(newValue);
      setValueWithCallback(newValue);
    } else if (mode === 'multiple' && Array.isArray(value)) {
      setOtherValue(newValue);

      setValue((prev: string[]) => {
        const filteredPrev = prev.filter((v) => v !== 'None of the above');

        const updatedArray = filteredPrev.includes(otherValue)
          ? filteredPrev.map((v) => (v === otherValue ? newValue : v))
          : [...filteredPrev, newValue];

        onChange?.(updatedArray);

        return updatedArray;
      });
    }
  };

  const handleOtherInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (mode === 'single' && typeof value === 'string') {
      setValueWithCallback('');
    } else {
      setValue((prev: string[]) => {
        const newValue = e.target.checked
          ? prev.filter((v) => v !== 'None of the above')
          : prev.filter((v) => v !== otherValue);

        onChange?.(newValue);

        return newValue;
      });
    }

    setOtherSelected(e.target.checked);
    setOtherValue('');
  };

  const handleNoneOfAboveChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.checked) return;

    setValueWithCallback(['None of the above']);
    setOtherSelected(false);
    setOtherValue('');
  };

  const handleOnRemoveOptionClick = (_: MouseEvent<HTMLButtonElement>, option: string) => {
    const newOptions = options.filter((o) => o !== option);
    setOptionsWithCallback(newOptions);

    if (mode === 'single' && value === option) {
      setValueWithCallback('');
    } else if (mode === 'multiple' && Array.isArray(value)) {
      const newValues = value.filter((v) => v !== option);
      setValueWithCallback(newValues);
    }
  };

  const isChecked = (option: string) => {
    if (mode === 'single') {
      if (!value) {
        return false;
      }

      return value === option;
    } else if (mode === 'multiple') {
      return value.includes(option);
    }
  };

  useEffect(() => {
    if (initialOptions) {
      setOptions(initialOptions);
    }
  }, [initialOptions]);

  useEffect(() => {
    if (initialValue) {
      setValue(initialValue);
    }
  }, [initialValue]);

  return (
    <section className={wrapperClassName}>
      <ul>
        {options.map((option, i) => (
          <li key={`option-${i}`} className='mb-2 flex items-center space-x-2'>
            <input
              type={mode === 'single' ? 'radio' : 'checkbox'}
              value={option}
              onChange={handleOnValueChange}
              checked={isChecked(option)}
              id={`option-${i}`}
              disabled={isEditable}
            />
            <div
              className={cn(
                'group flex w-full items-stretch overflow-hidden rounded border border-transparent focus-within:border-gray-400',
                { 'hover:border-gray-400': isEditable }
              )}
            >
              {isEditable ? (
                <>
                  <input
                    id={`option-${i}-label`}
                    type='text'
                    className='focus:outline-none m-1 w-full border-none p-0 text-sm focus:ring-0'
                    value={option}
                    onChange={(e) => handleOnOptionChange(e, i)}
                    onKeyDown={(e) => handleOnKeyDown(e, i)}
                    onFocus={() => setFocusedIndex(i)}
                    onBlur={() => setFocusedIndex(null)}
                    onPaste={(e) => handleOnPaste(e, i)}
                  />
                  <button
                    aria-label={`Remove option ${option}`}
                    type='button'
                    className={cn(
                      'focus:outline-none border-l border-gray-400 px-1 opacity-0 hover:bg-gray-50 focus:bg-gray-50 focus:opacity-100 group-hover:opacity-100',
                      {
                        'opacity-100': focusedIndex === i,
                        'cursor-not-allowed': options.length === 1 && !other
                      }
                    )}
                    onClick={(e) => handleOnRemoveOptionClick(e, option)}
                    disabled={options.length === 1 && !other}
                  >
                    <CloseSVG className='text-gray-500' />
                  </button>
                </>
              ) : (
                <Text as='label' htmlFor={`option-${i}`} className='m-1 w-full cursor-pointer text-sm'>
                  {option}
                </Text>
              )}
            </div>
          </li>
        ))}
        {other && !isEditable && (
          <>
            <div className='mb-2 flex flex-nowrap items-center space-x-3'>
              <input
                type={mode === 'single' ? 'radio' : 'checkbox'}
                name='Other'
                value='other'
                onChange={handleOtherInputChange}
                checked={otherSelected}
                id='other'
                aria-labelledby='other-label'
              />
              <Text as='label' htmlFor='other' id='other-label' className='m-1 w-full cursor-pointer text-sm'>
                Other
              </Text>
            </div>
            {otherSelected && (
              <input
                type='text'
                className='rounded-d mt-2 block w-full border border-gray-200 px-10 py-3'
                placeholder='Please specify'
                value={otherValue}
                onChange={(e) => handleOnOtherValueChange(e.target.value)}
                autoFocus
                name='Other field'
              />
            )}
          </>
        )}
        {mode === 'multiple' && none_of_above && !isEditable && (
          <>
            <div className='my-3 w-full border-t border-gray-200' />
            <div className='flex flex-nowrap items-center'>
              <div className='mb-2 flex flex-nowrap items-center space-x-3'>
                <input
                  type='radio'
                  name='None of the above'
                  value='None of the above'
                  onChange={handleNoneOfAboveChange}
                  checked={value.includes('None of the above')}
                  id='none_of_above'
                  aria-labelledby='none-of-above-label'
                />
                <Text
                  h='400'
                  as='label'
                  htmlFor='none_of_above'
                  id='none-of-above-label'
                  className='m-1 w-full cursor-pointer text-sm'
                >
                  None of the above
                </Text>
              </div>
            </div>
          </>
        )}
        {isEditable && (
          <>
            <li>
              <Text className='text-sm'>
                <button
                  type='button'
                  className='focus:outline-none text-sm text-indigo-600 hover:underline focus:underline'
                  onClick={() => addOptionAndFocus()}
                >
                  Enter option
                </button>
              </Text>
            </li>
            <div className='my-3 w-full border-t border-gray-200' />

            <li className='group relative flex items-center space-x-2'>
              <Toggle
                className='ml-0'
                testId='other'
                id='other'
                name='Other'
                on={other}
                onToggle={(other) => onChangeOther?.(other)}
              />
              <Text h='400' as='label' htmlFor='other'>
                Add "Other" answer type
              </Text>
              <Tooltip
                maxWidth={200}
                content='A multi-select option that includes an open form field for users to input additional information.'
              >
                <InfoCircleIcon className='h-4 w-4' />
              </Tooltip>
            </li>
            {mode === 'multiple' && (
              <li className='group relative flex items-center space-x-2'>
                <Toggle
                  testId='none-of-above'
                  className='ml-0'
                  id='none-of-above'
                  name='None of the above'
                  on={none_of_above}
                  onToggle={(none_of_above) => onChangeNoneOfAbove?.(none_of_above)}
                />
                <Text h='400' as='label' htmlFor='none-of-above'>
                  Add "None of the above" answer type
                </Text>
                <Tooltip
                  maxWidth={200}
                  className='self-center'
                  content='A mutually exclusive option that automatically deselects all other answers.'
                >
                  <InfoCircleIcon className='h-4 w-4' />
                </Tooltip>
              </li>
            )}
          </>
        )}
      </ul>
    </section>
  );
};
